import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-transparent outline-none border-b border-[#111111]/25 focus:border-[#1F9D55] transition-colors duration-300 py-4 text-lg placeholder:text-[#111111]/35";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("done");
      setForm({ name: "", email: "", message: "" });
      toast.success("Message sent — we'll get back to you within one business day.");
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send your message. Try again in a moment.");
    }
  };

  return (
    <section
      id="support"
      className="border-t border-[#111111]/10 px-6 md:px-12 py-24 md:py-40"
      data-testid="contact-section"
    >
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-16">
        <div className="md:col-span-5">
          <FadeUp>
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-[#16783F]">04 — Support</p>
            <h2 className="font-serif font-light tracking-tight text-4xl md:text-6xl mt-6">
              Talk to a <em className="text-[#1F9D55]">human.</em>
            </h2>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-[#111111]/70 max-w-sm">
              Questions, bugs, or feature wishes — we read everything that lands in our inbox, and a person replies
              within one business day.
            </p>
            <a
              href="mailto:support@myfreshtracker.app"
              data-testid="contact-email-link"
              className="mt-10 inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-semibold text-[#111111]/70 hover:text-[#16783F] transition-colors duration-300"
            >
              <Mail className="h-4 w-4" /> support@myfreshtracker.app
            </a>
          </FadeUp>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <FadeUp delay={0.15}>
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name" className="uppercase tracking-[0.2em] text-xs font-semibold text-[#111111]/50">
                  Your name
                </label>
                <input
                  id="contact-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Ada Lovelace"
                  className={inputClass}
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="uppercase tracking-[0.2em] text-xs font-semibold text-[#111111]/50">
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputClass}
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="uppercase tracking-[0.2em] text-xs font-semibold text-[#111111]/50">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us what's on your mind…"
                  className={`${inputClass} resize-none`}
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="contact-submit-button"
                className="rounded-full bg-[#111111] text-[#F7F6F2] px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-2 hover:bg-[#1F9D55] transition-colors duration-300 disabled:opacity-60"
              >
                {status === "loading" ? "Sending" : "Send message"}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
              {status === "done" && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm font-medium text-[#0A130B]"
                  data-testid="contact-success-message"
                >
                  Sent. We&rsquo;ll be in touch soon.
                </motion.p>
              )}
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
